import { VertexBuffer } from "@babylonjs/core/Buffers/buffer";
import { Geometry } from "@babylonjs/core/Meshes/geometry";
import { Mesh } from "@babylonjs/core/Meshes/mesh";
import { VertexData } from "@babylonjs/core/Meshes/mesh.vertexData";
import { Log } from "../../../utils";
import { fromDataUri } from "../../../utils/arraybuffer";
import { forEachParallel, ajax } from "../../../utils/async";
import { getFilePath, isDataUri, isNullOrEmpty } from "../../../utils/string";
import { GltfMesh, GltfTypes } from "./GltfMesh";
/** @internal */
var GltfMeshImport = /** @class */ (function () {
    function GltfMeshImport(pScene) {
        var _this = this;
        this.onBufferLoaded = function (pBufferId, pData) {
            if (pData == null || _this.mArrayBuffers == null) {
                Log.warn("Failed to load buffer " + pBufferId);
                return;
            }
            //bufferLength is optional in glTF v1.0
            var tBufferLength = _this.mGltf.buffers[pBufferId].byteLength;
            if (tBufferLength != null && pData.byteLength != tBufferLength) {
                Log.warn("Buffer " + pBufferId + " has length " + pData.byteLength +
                    ". Expected: " + tBufferLength);
            }
            _this.mArrayBuffers[pBufferId] = pData;
        };
        this.mScene = pScene;
        this.mConvertToLeftHanded = true;
        this.mIsVersion1 = false;
        this.mRootUrl = "";
        this.mArrayBuffers = null;
        this.mBinaryGltfOffset = 0;
    }
    GltfMeshImport.prototype.importGltf = function (pGltf, pGltfUrl) {
        var _this = this;
        if (pGltf == null || pGltfUrl == null)
            return (Promise.resolve(null));
        this.mRootUrl = getFilePath(pGltfUrl);
        this.mGltf = pGltf;
        this.prepareLoading();
        //load buffers first
        var tPromise = this.loadBuffers().then(function () {
            return (_this.importMeshes());
        });
        return (tPromise);
    };
    GltfMeshImport.prototype.importBinaryGltf = function (pBuffer) {
        if (pBuffer == null)
            return (null);
        var tResult = this.parseBinaryGltfHeader(pBuffer);
        if (tResult == null)
            throw new Error("Failed to load binary glTF");
        this.mGltf = tResult.gltf;
        this.prepareLoading();
        if (this.mIsVersion1)
            this.mArrayBuffers["KHR_binary_glTF"] = tResult.arrayBuffer;
        else
            this.mArrayBuffers.push(tResult.arrayBuffer);
        this.mBinaryGltfOffset = tResult.binaryOffset;
        return (this.importMeshes());
    };
    GltfMeshImport.LoadArrayBuffer = function (pUrl) {
        if (isNullOrEmpty(pUrl))
            return (Promise.resolve(null));
        return (ajax("GET", pUrl, null, { responseType: "arraybuffer" }));
    };
    GltfMeshImport.prototype.prepareLoading = function () {
        //check version
        this.mIsVersion1 = false;
        if (this.mGltf.asset.version == null || this.mGltf.asset.version.startsWith("1"))
            this.mIsVersion1 = true;
        //check if gltf uses a left handed coordinate system
        //this is necessary for backwards compatibility with old W-CF documents
        this.mConvertToLeftHanded = true;
        if (this.mGltf.asset.extras != null) {
            if (this.mGltf.asset.extras.EGR_leftHandedCS != null) {
                this.mConvertToLeftHanded = !this.mGltf.asset.extras.EGR_leftHandedCS;
            }
        }
        if (this.mIsVersion1)
            this.mArrayBuffers = {};
        else
            this.mArrayBuffers = new Array();
    };
    GltfMeshImport.prototype.importMeshes = function () {
        var tMeshes = new Array();
        if (this.mIsVersion1) {
            for (var tGltfMeshId in this.mGltf.meshes) {
                if (this.mGltf.meshes.hasOwnProperty(tGltfMeshId)) {
                    var tMesh = this.importMesh(this.mGltf.meshes[tGltfMeshId]);
                    if (tMesh != null)
                        tMeshes.push(tMesh);
                }
            }
        }
        else {
            for (var _i = 0, _a = this.mGltf.meshes; _i < _a.length; _i++) {
                var tGltfMesh = _a[_i];
                var tMesh = this.importMesh(tGltfMesh);
                if (tMesh != null)
                    tMeshes.push(tMesh);
            }
        }
        return (tMeshes);
    };
    GltfMeshImport.prototype.getStringFromTypedArray = function (pUInt8Array, pByteOffset, pByteLength) {
        var tOffset = pByteOffset != null ? pByteOffset : 0;
        var tLength = pByteLength != null ? pByteLength : pUInt8Array.byteLength - tOffset;
        tLength = Math.min(tLength, pUInt8Array.byteLength);
        var tSubArray = pUInt8Array.subarray(tOffset, tOffset + tLength);
        var tResult = "";
        for (var i = 0; i < tSubArray.length; ++i) {
            tResult += String.fromCharCode(tSubArray[i]);
        }
        return (tResult);
    };
    GltfMeshImport.prototype.parseBinaryGltfHeader = function (pArrayBuffer) {
        var tHeader = new Uint32Array(pArrayBuffer, 0, 5);
        //check header
        if (tHeader[0] !== GltfMesh._GLTF_HEADER_MAGIC) {
            Log.error("Not a valid binary glTF file");
            return (null);
        }
        //check version
        var tVersion = tHeader[1];
        if (tVersion !== 1 && tVersion !== 2) {
            Log.error("Invalid binary glTF version: " + tVersion);
            return (null);
        }
        var tContentLength = tHeader[3];
        var tContentOffset = 20;
        var tBinOffset = tContentOffset + tContentLength;
        if (tVersion > 1)
            tBinOffset += 8;
        var tJsonString = this.getStringFromTypedArray(new Uint8Array(pArrayBuffer), tContentOffset, tContentLength);
        //check if gltf has 4 byte alignment
        if (tBinOffset % 4 === 0) {
            //use buffer directly
            return {
                gltf: JSON.parse(tJsonString),
                arrayBuffer: pArrayBuffer,
                binaryOffset: tBinOffset
            };
        }
        else {
            //cut off header
            return {
                gltf: JSON.parse(tJsonString),
                arrayBuffer: pArrayBuffer.slice(tBinOffset),
                binaryOffset: 0
            };
        }
    };
    GltfMeshImport.prototype.loadBuffers = function () {
        var _this = this;
        //load up to 5 buffers parallel
        var tLoadTasks = new Array();
        if (this.mIsVersion1) {
            var _loop_1 = function (tBuffer) {
                tLoadTasks.push(function () { return _this.loadBuffer(tBuffer); });
            };
            for (var tBuffer in this.mGltf.buffers) {
                _loop_1(tBuffer);
            }
        }
        else {
            var _loop_2 = function (i) {
                tLoadTasks.push(function () { return _this.loadBuffer(i); });
            };
            for (var i = 0; i < this.mGltf.buffers.length; ++i) {
                _loop_2(i);
            }
        }
        return (forEachParallel(tLoadTasks, function (pIndex, pTask) { return pTask(); }, 5).then(function () { }));
    };
    GltfMeshImport.prototype.loadBuffer = function (pBufferId) {
        var tBuffer = this.mGltf.buffers[pBufferId];
        if (tBuffer == null)
            return (Promise.reject(new Error("Buffer " + pBufferId + " not found")));
        if (isDataUri(tBuffer.uri)) {
            var tArrayBuffer = fromDataUri(tBuffer.uri);
            this.onBufferLoaded(pBufferId, tArrayBuffer);
            return (Promise.resolve());
        }
        else {
            return (this.loadBufferFromUrl(pBufferId, this.mRootUrl + tBuffer.uri));
        }
    };
    GltfMeshImport.prototype.loadBufferFromUrl = function (pBufferId, pUrl) {
        var _this = this;
        if (isNullOrEmpty(pUrl))
            return (Promise.resolve());
        var tPromise = GltfMeshImport.LoadArrayBuffer(pUrl).then(function (pBuffer) {
            _this.onBufferLoaded(pBufferId, pBuffer);
        });
        return (tPromise);
    };
    GltfMeshImport.prototype.importMesh = function (pGltfMesh) {
        if (pGltfMesh == null)
            return (null);
        var tVertexData = new VertexData();
        // Positions, normals and UVs
        for (var i = 0; i < pGltfMesh.primitives.length; i++) {
            var tCurrentVertexData = i === 0 ? tVertexData : new VertexData();
            var tPrimitive = pGltfMesh.primitives[i];
            if (tPrimitive.mode != null && tPrimitive.mode !== 4) {
                continue;
            }
            var tAttributes = tPrimitive.attributes;
            var tAccessor = null;
            var tBuffer = null;
            // Set positions, normal and uvs
            for (var tSemantic in tAttributes) {
                // Link accessor and buffer view
                tAccessor = this.mGltf.accessors[tAttributes[tSemantic]];
                tBuffer = this.getBufferFromAccessor(tAccessor);
                if (tSemantic === "NORMAL") {
                    tCurrentVertexData.normals = this.bufferToFloat32Array(tBuffer, this.mConvertToLeftHanded);
                }
                else if (tSemantic === "POSITION") {
                    tCurrentVertexData.positions = this.bufferToFloat32Array(tBuffer, this.mConvertToLeftHanded);
                }
                else if (tSemantic.indexOf("TEXCOORD_") !== -1) {
                    var tChannel = Number(tSemantic.split("_")[1]);
                    var tUvKind = VertexBuffer.UVKind + (tChannel === 0 ? "" : (tChannel + 1));
                    tCurrentVertexData.set(this.bufferToFloat32Array(tBuffer, false), tUvKind);
                }
            }
            // Indices
            if (tPrimitive.indices != null) {
                tAccessor = this.mGltf.accessors[tPrimitive.indices];
                tBuffer = this.getBufferFromAccessor(tAccessor);
                if (tBuffer instanceof Uint16Array || tBuffer instanceof Uint32Array)
                    tCurrentVertexData.indices = tBuffer;
                else
                    tCurrentVertexData.indices = new Uint32Array(tBuffer);
            }
            else {
                //generate indices
                if (tCurrentVertexData.positions != null) {
                    var tLength = tCurrentVertexData.positions.length / 3;
                    tCurrentVertexData.indices = new Uint32Array(tLength);
                    for (var i_1 = 0; i_1 < tLength; ++i_1)
                        tCurrentVertexData.indices[i_1] = i_1;
                }
            }
            // merge primitives
            if (i > 0) {
                tVertexData.merge(tCurrentVertexData);
            }
        }
        //finally create mesh and geometry
        var tMesh = new Mesh("", this.mScene);
        new Geometry(Geometry.RandomId(), this.mScene, tVertexData, false, tMesh);
        return (tMesh);
    };
    GltfMeshImport.prototype.bufferToFloat32Array = function (pBuffer, pNegateZ) {
        var tArray = (pBuffer instanceof Float32Array) ? pBuffer :
            new Float32Array(pBuffer);
        if (pNegateZ) { //right to left handed
            for (var j = 0; j < tArray.length; j += 3) {
                //negate z
                tArray[j + 2] = -tArray[j + 2];
            }
        }
        return (tArray);
    };
    GltfMeshImport.prototype.getByteStrideFromType = function (pAccessor) {
        if (pAccessor.byteStride != null)
            return (pAccessor.byteStride);
        switch (pAccessor.type) {
            case "VEC2":
                return (2);
            case "VEC3":
                return (3);
            default:
                return (1);
        }
    };
    GltfMeshImport.prototype.getBufferFromAccessor = function (pAccessor) {
        if (this.mArrayBuffers == null)
            throw new Error("undefined mArrayBuffers");
        if (pAccessor.bufferView == null)
            throw new Error("undefined bufferView");
        var tBufferView = this.mGltf.bufferViews[pAccessor.bufferView];
        var tArrayBuffer = this.mArrayBuffers[tBufferView.buffer];
        var tAccessorOffset = pAccessor.byteOffset != null ? pAccessor.byteOffset : 0;
        var tBufferViewOffset = tBufferView.byteOffset != null ? tBufferView.byteOffset : 0;
        var tByteOffset = this.mBinaryGltfOffset + tAccessorOffset + tBufferViewOffset;
        var tCount = pAccessor.count * this.getByteStrideFromType(pAccessor);
        switch (pAccessor.componentType) {
            case GltfTypes.ComponentType.BYTE:
                return (new Int8Array(tArrayBuffer, tByteOffset, tCount));
            case GltfTypes.ComponentType.UNSIGNED_BYTE:
                return (new Uint8Array(tArrayBuffer, tByteOffset, tCount));
            case GltfTypes.ComponentType.SHORT:
                return (new Int16Array(tArrayBuffer, tByteOffset, tCount));
            case GltfTypes.ComponentType.UNSIGNED_SHORT:
                return (new Uint16Array(tArrayBuffer, tByteOffset, tCount));
            case GltfTypes.ComponentType.UNSIGNED_INT:
                return (new Uint32Array(tArrayBuffer, tByteOffset, tCount));
            default:
                return (new Float32Array(tArrayBuffer, tByteOffset, tCount));
        }
    };
    return GltfMeshImport;
}());
export { GltfMeshImport };
